/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IIconProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { INavLink, INavLinkGroup, INavProps, INavStyles, Nav } from '@fluentui/react/lib/Nav';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { Intent } from 'src/luis/modules/intents/Intent';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { EntityRole } from 'src/luis/projects/entities/types/EntityRole';
import {
    getAllConfusionCount,
    getCorrectConfusionsCount,
    ModelConfusions
} from 'src/luis/projects/testPane/utils/batchTestResultsCalculator';
import { Model } from 'src/luis/types/Model';
import { LuisId } from 'src/luis/types/Resource';

export type ModelListProps = {
    /**
     * The calculated model confusions.
     */
    modelConfusions: ModelConfusions;

    /**
     * The currently selected model if for expansion.
     */
    selectedModelId: LuisId;

    /**
     * The intents in the application.
     */
    intents: Intent[];

    /**
     * The entities in the application.
     */
    entities: Entity[];

    /**
     * A callback for when the model is clicked.
     */
    onModelClicked: (modelId: LuisId) => void;
};

const theme = getScopedTheme('ModelList');

const navStyles = classNamesFunction<INavProps, INavStyles>()({
    root: { border: theme.border, height: theme.height },
    groupContent: { marginBottom: 0 }
});

/**
 * Renders a sidebar of models with their batch
 * test results.
 */
export const ModelList = (props: ModelListProps) => {
    const { modelConfusions, intents, entities, onModelClicked, selectedModelId } = props;

    const correct = React.useMemo(() => getCorrectConfusionsCount(modelConfusions), [modelConfusions]);
    const total = React.useMemo(() => getAllConfusionCount(modelConfusions), [modelConfusions]);

    const getModelName = (model: Model | EntityRole): string => `${model.name} (${correct.get(model.id)}/${total.get(model.id)})`;
    const getModelIcon = (m: Model | EntityRole) => (correct.get(m.id) === total.get(m.id) ? 'CompletedSolid' : 'StatusErrorFull');
    const getModelIconColor = (m: Model | EntityRole) => (correct.get(m.id) === total.get(m.id) ? theme.correctColor : theme.failColor);

    const navGroups: INavLinkGroup[] = [
        {
            name: 'Intents',
            links: intents.map<INavLink>(i => ({
                key: i.id as string,
                name: getModelName(i),
                iconProps: { iconName: getModelIcon(i), styles: getIconStyles(getModelIconColor(i)) },
                url: '#',
                onClick: () => onModelClicked(i.id)
            }))
        },
        {
            name: 'Entities',
            links: entities.map<INavLink>(e => ({
                key: e.id as string,
                name: getModelName(e),
                iconProps: { iconName: getModelIcon(e), styles: getIconStyles(getModelIconColor(e)) },
                url: '#',
                onClick: () => onModelClicked(e.id),
                links: e.roles.map<INavLink>(r => ({
                    key: r.id as string,
                    name: getModelName(r),
                    iconProps: { iconName: getModelIcon(r), styles: getIconStyles(getModelIconColor(r)) },
                    url: '#',
                    onClick: () => onModelClicked(r.id)
                }))
            }))
        }
    ];

    return <Nav styles={navStyles} groups={navGroups} selectedKey={selectedModelId as string} />;
};

/**
 * A helper function to get the icon styles
 * for the state buttons.
 *
 * @param color The color to give to the icon.
 */
const getIconStyles = (color: string) => classNamesFunction<IIconProps, IIconStyles>()({ root: { color: `${color} !important` } });
